import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@mint-lib/query-client';
import { Chat, Schedule } from '@mint-sdk/api-ai-assistant';

export const getQueryScheduleOption = () => {
  return queryOptions(Schedule.getScheduleOptions({}));
};

export const useCreateScheduleMutation = () => {
  const queryClient = useQueryClient();
  const { queryKey: getScheduledQueryKey } = Schedule.getScheduleOptions({});

  return useMutation({
    mutationFn: (data: Schedule.CreateScheduleRequestType) =>
      Schedule.createQuerySchedule({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getScheduledQueryKey,
      });

      Chat.getChatList.invalidate(queryClient, { params: {} });

      const { queryKey: getChatListKey } = Chat.getChatList({
        params: {},
      });
      queryClient.invalidateQueries({
        queryKey: getChatListKey,
      });
    },
  });
};

export type EditSchedulePayload = Omit<
  Schedule.QueryScheduleSchemaType,
  'query'
> & { query?: string };

export const useEditScheduleMutation = () => {
  const queryClient = useQueryClient();
  const { queryKey: getScheduledQueryKey } = Schedule.getScheduleOptions({});

  return useMutation({
    mutationFn: (data: EditSchedulePayload) =>
      Schedule.editQuerySchedule({
        urlParams: { scheduledQueryId: data.scheduledQueryId },
        data,
      }),
    onSuccess: (data: Schedule.QueryScheduleSchemaType) => {
      const { queryKey: getHistoryByIdKey } = Chat.getHistoryById({
        urlParams: { chatSessionId: data.chatSessionId },
      });

      queryClient.invalidateQueries({
        queryKey: getHistoryByIdKey,
      });

      queryClient.invalidateQueries({
        queryKey: getScheduledQueryKey,
      });

      Chat.getChatList.invalidateAll(queryClient, {});
    },
  });
};

export const useDeleteScheduledQueryMutation = () => {
  const queryClient = useQueryClient();
  const { queryKey: getScheduledQueryKey } = Schedule.getScheduleOptions({});

  return useMutation({
    mutationFn: (scheduledQueryId: string) =>
      Schedule.deleteQuerySchedule({ urlParams: { scheduledQueryId } }),
    onMutate: async (queryId) => {
      const previousQueries: Schedule.QueryScheduleSchemaType[] | undefined =
        queryClient.getQueryData(getScheduledQueryKey);
      queryClient.setQueryData(
        getScheduledQueryKey,
        (old?: Schedule.QueryScheduleSchemaType[]) =>
          old?.filter((f) => f.scheduledQueryId !== queryId),
      );

      return { previousQueries };
    },
    onSuccess: async () => {
      const { queryKey: getChatListKey } = Chat.getChatList({
        params: {},
      });
      queryClient.invalidateQueries({
        queryKey: getChatListKey,
      });
    },
  });
};
